import {
  Box,
  Flex,
  FormControl,
  Heading,
  HStack,
  Switch,
  Text,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { HiOutlineDocumentSearch } from "react-icons/hi";

import {
  LoadingIndicator,
  SearchInput,
  SortableTable,
  SortableTableColumn,
  useToast,
} from "../../../../components";
import { EmptyStateWithIcon } from "../../../../components/EmptyStates";
import useOffsetPagination from "../../../../hooks/useOffsetPagination";
import { formatDate } from "../../../../utils/datetime";
import {
  OrgInterviewLocationsPaginatedQuery,
  useOrgInterviewLocationsPaginatedLazyQuery,
  useUpdateInterviewLocationAutoJoinMutation,
} from "../../../graphql";
import RecordingSettingsPopover from "./RecordingSettingsPopover";
import { SettingSwitchWithConfirmDialogue } from "./SettingSwitchWithConfirmDialogue";

type InterviewLocation = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<
          OrgInterviewLocationsPaginatedQuery["currentUser"]
        >["organization"]
      >["interviewLocationsPag"]
    >["results"]
  >[number]
>;

const LocationRecordingSettings: React.FC = () => {
  const toast = useToast();

  const [getLocations, { data, loading }] =
    useOrgInterviewLocationsPaginatedLazyQuery({
      fetchPolicy: "network-only",
      onError: (err) => {
        toast({
          title: "Error",
          description: `Failed to load locations: ${err.message}`,
          status: "error",
        });
      },
      onCompleted: ({ currentUser }) => {
        if (currentUser?.organization?.interviewLocationsPag) {
          const clientList = currentUser.organization.interviewLocationsPag;
          setNumberOfPages(
            currentUser.organization.interviewLocationsPag.pageInfo
              ?.totalPages || 1
          );
          setListItems(clientList.results ?? []);
        }
      },
    });

  const {
    listItems: locations,
    pageOptions,
    setSearchTerm,
    setListItems,
    setNumberOfPages,
    searchTerm,
    reload,
  } = useOffsetPagination<InterviewLocation>(getLocations, {
    initialSortBy: { id: "createdAt", desc: true },
  });

  const atsOptInEnabled =
    data?.currentUser?.organization?.atsOptInEnabled ?? false;

  const [updateInterviewLocationAutoJoin] =
    useUpdateInterviewLocationAutoJoinMutation({
      onError: (err) => {
        toast({
          status: "error",
          title: "Error",
          description: `Failed to turn on Auto Join New Positions: ${err.message}`,
        });
      },
      onCompleted: (data) => {
        if (
          data?.updateInterviewLocationAutoJoin &&
          data?.updateInterviewLocationAutoJoin.interviewLocations.length > 0
        ) {
          toast({
            title: "Success",
            description: `Successfully updated recording settings for the ${data?.updateInterviewLocationAutoJoin.interviewLocations[0].name} location`,
            status: "success",
          });
          reload();
        }
      },
    });

  const handleUpdateLocationAutoJoin = (
    locations: InterviewLocation[],
    autoJoinInterview: boolean,
    optIn: boolean
  ): Promise<any> => {
    const interviewLocationIds = locations.map((lc) => lc.id);
    return updateInterviewLocationAutoJoin({
      variables: { interviewLocationIds, autoJoinInterview, optIn },
    });
  };

  const columns: SortableTableColumn<InterviewLocation>[] = [
    {
      Header: "Name",
      accessor: "name",
      id: "name",
    },
    {
      Header: "Added",
      accessor: "createdAt",
      Cell: ({
        row: { original: location },
      }: {
        row: { original: InterviewLocation };
      }) => (
        // cspell: disable-next-line
        <Text style={{ fontVariantNumeric: "tabular-nums" }} maxW="102px">
          {formatDate(location.createdAt, {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
          })}
        </Text>
      ),
    },
    {
      Header: "Record",
      accessor: "autoJoinAllNewPositions",
      Cell: ({
        row: { original: location },
      }: {
        row: { original: InterviewLocation };
      }) => {
        const [loading, setLoading] = useState(false);
        return (
          <HStack align="baseline" spacing="3" maxW="120px">
            {loading ? (
              <LoadingIndicator
                size="xs"
                alignSelf="flex-start"
                width="inherit"
              />
            ) : (
              <FormControl>
                <Switch
                  isChecked={location.autoJoinAllNewPositions}
                  colorScheme="prism"
                  display="flex"
                  alignItems="center"
                  fontSize="sm"
                  lineHeight={1.5}
                  onChange={(e) => {
                    setLoading(true);
                    const newValue = e.target.checked;
                    handleUpdateLocationAutoJoin(
                      [location],
                      newValue,
                      location.atsOptInAllNewPositions
                    )
                      .then(() => {
                        setLoading(false);
                      })
                      .catch(() => {
                        setLoading(false);
                      });
                  }}
                >
                  {location.autoJoinAllNewPositions
                    ? "Auto-join enabled"
                    : "Auto-join disabled"}
                </Switch>
              </FormControl>
            )}
          </HStack>
        );
      },
    },
    {
      Header: "Positions",
      accessor: "positionsCount",
    },
    {
      Header: "Bulk Actions",
      Cell: ({
        row: { original: location },
      }: {
        row: { original: InterviewLocation };
      }) => {
        return (
          <HStack mb="3">
            <RecordingSettingsPopover
              searchTerm={null}
              clientId={null}
              locationId={location.id}
              positionsCount={location.positionsCount ?? 0}
              disabled={!location.positionsCount}
              showOptIn={atsOptInEnabled}
            />
          </HStack>
        );
      },
    },
  ];

  if (atsOptInEnabled) {
    const optInColumn: SortableTableColumn<InterviewLocation> = {
      Header: "Consent",
      accessor: "atsOptInAllNewPositions",
      Cell: ({
        row: { original: location },
      }: {
        row: { original: InterviewLocation };
      }) => {
        return (
          <SettingSwitchWithConfirmDialogue
            isChecked={location.atsOptInAllNewPositions}
            onToggle={(checked) => {
              return handleUpdateLocationAutoJoin(
                [location],
                location.autoJoinAllNewPositions,
                checked
              );
            }}
            shouldConfirm={!location.atsOptInAllNewPositions}
            headerText="Are you sure you want to enable opt-in consent for all future positions?"
            headerBody={
              <>
                Enabling <strong>Opt In consent</strong> will immediately apply
                opt-in rules to all active candidates. Any candidate not
                currently assigned to an opt-in position will be automatically
                marked as Opted Out, which may disrupt scheduling and generate a
                large volume of opt-out notification emails. This change may be
                difficult to reverse.
              </>
            }
            confirmButtonText="Proceed Anyways"
          >
            {location.atsOptInAllNewPositions
              ? "Opt-in consent"
              : "Opt-out consent"}
          </SettingSwitchWithConfirmDialogue>
        );
      },
    };
    columns.splice(columns.length - 3, 0, optInColumn);
  }

  return (
    <Box w="fit-content" mt="4">
      <Heading as="h1" size="md" mb="4">
        Location Recording Settings
      </Heading>
      <Box>
        <Flex
          alignItems="left"
          mb={5}
          flexFlow="column nowrap"
          id="search-filter--search"
        >
          <SearchInput
            placeholder="Search locations by name"
            defaultValue={searchTerm}
            onSearch={setSearchTerm}
            width="508px"
            maxWidth="508px"
          />
        </Flex>
      </Box>
      {loading || locations.length > 0 ? (
        <SortableTable<InterviewLocation>
          columns={columns}
          columnWidths={["102px", "50px", "102px", "100px"]}
          data={locations}
          autoResetSortBy={false}
          initialSort={{ id: "createdAt", desc: true }}
          pageOptions={pageOptions}
          manualPagination
          width="1200px"
          loading={loading}
        />
      ) : (
        <EmptyStateWithIcon
          maxWidth="1200px"
          heading="No locations found"
          icon={HiOutlineDocumentSearch}
          Text={() => (
            <Box>
              No locations were found{" "}
              {searchTerm
                ? " that match your query. Please try something else."
                : "."}
            </Box>
          )}
          padded={false}
        />
      )}
    </Box>
  );
};

export default LocationRecordingSettings;
